import { Controller } from '@hotwired/stimulus'
import type { Chart as ChartInstanceType, ChartType, ChartItem, ChartConfiguration, ChartOptions } from 'chart.js'
import type { CustomerJourneyData, CustomerJourneyStatistic, CustomerJourneyVisit } from '../types/customer_journey'
import ChartColours from '../helpers/chart_colours'
import { Chart } from '../chart-js-sankey'
import { Chart as ChartJS } from 'chart.js'

// Connects to data-controller="ex-customer-journey"

export default class extends Controller {
  static targets = ['canvas', 'leastPopularTitle', 'leastPopularValue', 'mostPopularTitle', 'mostPopularValue', 'pieCanvas', 'tableBody']
  static values = { data: Array, pdf: { type: Boolean, default: false } }

  declare readonly leastPopularTitleTarget: HTMLElement
  declare readonly leastPopularValueTarget: HTMLElement
  declare readonly mostPopularTitleTarget: HTMLElement
  declare readonly mostPopularValueTarget: HTMLElement
  declare readonly canvasTarget: HTMLCanvasElement
  declare readonly dataValue: CustomerJourneyData[]
  declare readonly pieCanvasTarget: HTMLCanvasElement
  declare readonly tableBodyTarget: HTMLTableSectionElement
  declare readonly pdfValue: boolean

  declare readonly hasLeastPopularTitleTarget: boolean
  declare readonly hasLeastPopularValueTarget: boolean
  declare readonly hasMostPopularTitleTarget: boolean
  declare readonly hasMostPopularValueTarget: boolean
  declare readonly hasPieCanvasTarget: boolean
  declare readonly hasTableBodyTarget: boolean

  declare chartInstance: ChartInstanceType
  declare chart: ChartType
  declare selectedData: CustomerJourneyStatistic[]
  declare selectedPeriodIndex: number
  declare selectedTargetType: keyof CustomerJourneyData
  declare selectedVisits: keyof CustomerJourneyVisit
  declare mappedColours: any
  declare deselectedKeys: string[]
  declare pieChartInstance?: ChartJS<'pie', number[], string>

  mapDataToColours (): void {
    const fromKeys = new Set()

    this.dataValue.forEach((d: CustomerJourneyData) => {
      Object.values(d).forEach((nestedObject: CustomerJourneyVisit) => {
        ['all', 'engaged'].forEach(key => {
          nestedObject[key as keyof CustomerJourneyVisit].forEach((item: CustomerJourneyStatistic) => fromKeys.add(item.fromKey))
        })
      })
    })
    const colourMap: any = {}
    Array.from(fromKeys).forEach((target: any, index: number) => { colourMap[target] = ChartColours[index % ChartColours.length] })
    this.mappedColours = colourMap
  }

  // Hack to ensure only the first nodes label is displayed (Sankey library does not provide a simple way of doing this)
  setLabels (): any {
    const labels: any = {}
    this.selectedData.forEach((item: CustomerJourneyStatistic) => {
      if (item.from === `${item.fromKey} (0)`) {
        const label = item.fromKey.length > 14 ? `${item.fromKey.substring(0, 14)}...` : item.fromKey
        labels[item.from] = label
      } else {
        labels[item.from] = ' '
        labels[item.to] = ' '
      }
    })

    return labels
  }

  formattedChartData (): any[] {
    return [{
      data: this.selectedData,
      colorFrom: ({ raw: { fromKey } }: any) => this.mappedColours[fromKey],
      colorTo: ({ raw: { toKey } }: any) => this.mappedColours[toKey],
      colorMode: 'from',
      size: 'max',
      color: ({ raw: { fromKey } }: any) => this.mappedColours[fromKey],
      labels: this.setLabels()
    }]
  }

  setCustomerJourneyStatistics ({ detail }: any): void {
    this.selectedPeriodIndex = detail.periodIndex
    this.selectedVisits = detail.visits === 'visits_count' ? 'all' : 'engaged'

    this.updateChart()
  }

  setTargetType ({ target }: any): void {
    this.selectedTargetType = this.selectedTargetType === 'ExAllocation' ? 'ExGroup' : 'ExAllocation'
    target.innerHTML = this.selectedTargetType === 'ExAllocation' ? 'Show EX Groups' : 'Show EX Sensors'
    this.dispatch('setTargetType', { detail: { selectedTargetType: this.selectedTargetType } })
    this.updateChart()
  }

  setSelectedData (): void {
    this.selectedData =
      this.dataValue[this.selectedPeriodIndex][this.selectedTargetType][this.selectedVisits].filter(item => !this.deselectedKeys.includes(item.fromKey) && !this.deselectedKeys.includes(item.toKey))
  }

  updateChart (): void {
    this.setSelectedData()
    this.chartInstance.data.datasets = this.formattedChartData()
    this.chartInstance.update()
    this.updateHeroStats()
    this.renderPieChart()
  }

  filterSelectedData (key: string): void {
    const allKeys = [...new Set(this.selectedData.map((item: any): string => item.fromKey))]

    if (this.deselectedKeys.includes(key)) {
      this.deselectedKeys = this.deselectedKeys.filter(k => k !== key)
    } else {
      // Ensure at least 2 targets are displayed
      if (allKeys.length < 3) return

      this.deselectedKeys.push(key)
    }
    this.updateChart()
  }

  updateHeroStats (): { allKeys: string[], flowTally: Record<string, number> } {
    const allKeys = [...new Set(this.selectedData.map((item: any): string => item.fromKey))]

    // All interaction 0's for current selection
    const firstInteractionsIndex = allKeys.length * (allKeys.length - 1)
    const firstInteractions = this.selectedData.slice(0, firstInteractionsIndex)

    const flowTally: Record<string, number> = {}
    allKeys.forEach((k: string) => {
      flowTally[k] = firstInteractions.filter((d) => d.fromKey === k).reduce((a, item) => a + item.flow, 0)
    })
    const totalFlow = Object.values(flowTally).reduce((accumulator: number, value: number) => accumulator + value, 0)

    Object.keys(flowTally).forEach((k: string) => {
      flowTally[k] = flowTally[k] / totalFlow * 100
    })

    const sorted = Object.keys(flowTally).sort((a, b) => { return -(flowTally[a] - flowTally[b]) })

    if (this.hasMostPopularTitleTarget) this.mostPopularTitleTarget.innerHTML = sorted[0] ?? ''
    if (this.hasMostPopularValueTarget) this.mostPopularValueTarget.innerHTML = `${Math.round(flowTally[sorted[0]] ?? 0)}%`

    const least = sorted.slice(-1)[0]
    if (this.hasLeastPopularTitleTarget) this.leastPopularTitleTarget.innerHTML = least ?? ''
    if (this.hasLeastPopularValueTarget) this.leastPopularValueTarget.innerHTML = `${Math.round(flowTally[least] ?? 0)}%`

    return { allKeys, flowTally }
  }

  destroyPieChart (): void {
    if (this.pieChartInstance != null) {
      this.pieChartInstance.destroy()
      this.pieChartInstance = undefined
    }
  }

  renderTable (labels: string[], values: number[], colors: string[]): void {
    if (!this.hasTableBodyTarget) return
    const numberFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 })
    const rowsHtml = labels.map((label, i) => {
      const value = typeof values[i] === 'number' && Number.isFinite(values[i]) ? values[i] : 0
      const color = colors[i] ?? '#000000'

      return `
        <tr>
          <td class="p-2 border-b border-stone-200">
            <span
              class="inline-block align-middle w-4 h-4 rounded-sm mr-2 print-retain-bg-color"
              style="background:${color}; border:1px solid rgba(0,0,0,.15)"></span>
          </td>
          <td class="p-2 border-b border-stone-200">${label}</td>
          <td class="p-2 border-b border-stone-200 text-right">${numberFormat.format(value)}%</td>
        </tr>
      `
    }).join('')

    this.tableBodyTarget.innerHTML = rowsHtml
  }

  renderPieChart (): void {
    const { allKeys, flowTally } = this.updateHeroStats()
    if (allKeys.length === 0) { this.destroyPieChart(); return }

    const rows = allKeys.map((label) => ({ label, value: flowTally[label] ?? 0, color: this.mappedColours[label] ?? '#000000' })).sort((a, b) => b.value - a.value)
    const labels = rows.map(r => r.label)
    const values = rows.map(r => r.value)
    const backgroundColor = rows.map(r => r.color)
    this.renderTable(labels, values, backgroundColor)

    if (!this.hasPieCanvasTarget) return
    const ctx = this.pieCanvasTarget.getContext('2d')
    if (ctx == null) return

    const baseOptions: ChartOptions<'pie'> = {
      responsive: true,
      maintainAspectRatio: false
    }

    const optionsForPDF: ChartOptions<'pie'> = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      animations: { numbers: { duration: 0 }, colors: { duration: 0 } },
      transitions: {
        active: { animation: { duration: 0 } },
        resize: { animation: { duration: 0 } }
      }
    }

    const config: ChartConfiguration<'pie', number[], string> = {
      type: 'pie',
      data: {
        labels,
        datasets: [{
          label: 'Start Points (%)',
          data: values,
          backgroundColor,
          borderColor: backgroundColor,
          borderWidth: 1
        }]
      },
      options: {
        ...(this.pdfValue ? optionsForPDF : baseOptions),
        plugins: {
          legend: { position: 'bottom' },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const raw = ctx.parsed
                const percentage = (typeof raw === 'number' && Number.isFinite(raw)) ? raw : 0
                const label = ctx.label ?? ''
                const nf = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 })
                return `${label}: ${nf.format(percentage)}%`
              }
            }
          }
        }
      }
    }

    this.destroyPieChart()
    this.pieChartInstance = new ChartJS<'pie', number[], string>(ctx, config)
  }

  connect (): void {
    // Make whole time engaged the default selection
    this.selectedPeriodIndex = 0
    this.selectedTargetType = 'ExAllocation'
    this.selectedVisits = 'engaged'
    this.deselectedKeys = []
    this.setSelectedData()
    this.mapDataToColours()
    this.updateHeroStats()
    this.renderPieChart()

    const ctx: ChartItem | null = this.canvasTarget.getContext('2d')

    if (ctx === null) return
    // @ts-expect-error: Allow customSankey as chart type for options
    const baseOptionsCustomerJourney: ChartOptions<'customSankey'> = {
      responsive: true,
      maintainAspectRatio: true
    }
    // @ts-expect-error: Allow customSankey as chart type for options
    const optionsForPDFCustomerJourney: ChartOptions<'customSankey'> = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false
    }

    // eslint-disable-next-line no-new
    this.chartInstance = new Chart(ctx, {
      // @ts-expect-error: Allow customSankey as chart type
      type: 'customSankey',
      data: {
        datasets: this.formattedChartData()
      },
      // @ts-expect-error: Allow customSankey as chart type for options
      options: {
        ...(this.pdfValue ? optionsForPDFCustomerJourney : baseOptionsCustomerJourney),
        layout: {
          padding: {
            left: 120
          }
        },
        plugins: {
          legend: {
            display: true,
            onClick: (_a, legendItem, _c) => {
              this.filterSelectedData(legendItem.text)
            },
            labels: {
              generateLabels: (): any => {
                const labels: Array<{ text: string, fillStyle: string, hidden: boolean }> = []
                this.dataValue[this.selectedPeriodIndex][this.selectedTargetType][this.selectedVisits].forEach(({ fromKey }: CustomerJourneyStatistic): any => {
                  if (!labels.map(l => l?.text).includes(fromKey)) {
                    labels.push({
                      text: fromKey,
                      fillStyle: this.mappedColours[fromKey],
                      hidden: this.deselectedKeys.includes(fromKey)
                    })
                  }
                })
                return labels
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const fromKey: string = context.dataset.data[context.dataIndex].fromKey
                const toKey: string = context.dataset.data[context.dataIndex].toKey
                return `${fromKey} -> ${toKey}`
              }
            }
          }
        }
      }
    })
  }
}
